import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import TaskStatusChart from './Dashboard/TaskStatusChart';
import AverageHoursChart from './Dashboard/AverageHoursChart';
import MoodDistributionChart from './Dashboard/MoodDistributionChart';
import { transformTaskStatusData, transformAverageHoursData, transformMoodData } from '../utils/chartDataTransform';
import { 
  Trophy, Medal, Flame, Star, Target, TrendingUp, Users, Building2,
  Award, Clock, CheckCircle, Play, Calendar, ChevronRight, BarChart3
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mood emoji mapper
const getMoodEmoji = (mood) => {
  const moodMap = {
    'Happy': '😊',
    'Inspired': '✨',
    'Overworked': '😰',
    'Bored': '😑',
    'Low': '😔',
    'very_happy': '😊',
    'happy': '😊',
    'neutral': '😐',
    'sad': '😔',
    'very_sad': '😢'
  };
  return moodMap[mood] || '😐';
};

const BADGES = {
  first_session: { name: 'First Session', icon: '🎯', description: 'Completed your first session', color: '#10B981' },
  five_day_streak: { name: '5-Day Streak', icon: '🔥', description: 'Logged activity 5 days in a row', color: '#F59E0B' }
};

const Analytics = ({ user }) => {
  const [activeMainTab, setActiveMainTab] = useState('analytics'); // 'analytics' or 'rewards'
  const [analytics, setAnalytics] = useState(null);
  const [taskAnalytics, setTaskAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Analytics state
  const getDefaultDateRange = () => {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    return {
      from_date: sevenDaysAgo.toISOString().split('T')[0],
      to_date: today.toISOString().split('T')[0]
    };
  };
  
  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [selectedUser, setSelectedUser] = useState('');
  const [users, setUsers] = useState([]);
  const [teamMemberScans, setTeamMemberScans] = useState([]);

  // Rewards state
  const [leaderboardTab, setLeaderboardTab] = useState('global');
  const [leaderboard, setLeaderboard] = useState([]);
  const [userStats, setUserStats] = useState({
    points: 0, rank: 0, streak: 0, badges: [],
    activity_summary: { sessions_completed: 0, daily_scans_submitted: 0, tasks_completed: 0, total_time_hours: 0 }
  });
  const [habits, setHabits] = useState([]);

  useEffect(() => {
    if (user.role !== 'team_member') {
      fetchUsers();
    }
    fetchAnalytics();
    fetchTaskAnalytics();
    fetchTeamMemberScans();
    fetchRewardsData();
  }, []);

  useEffect(() => {
    if (selectedUser !== '') {
      fetchAnalytics();
      fetchTaskAnalytics();
      fetchTeamMemberScans();
    }
  }, [selectedUser]);

  useEffect(() => {
    fetchRewardsData();
  }, [leaderboardTab]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (dateRange.from_date) params.from_date = dateRange.from_date;
      if (dateRange.to_date) params.to_date = dateRange.to_date;
      if (selectedUser) params.user_id = selectedUser;

      const response = await axios.get(`${API_URL}/analytics/summary`, {
        headers: { Authorization: `Bearer ${token}` }, params
      });
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchTaskAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (dateRange.from_date) params.from_date = dateRange.from_date;
      if (dateRange.to_date) params.to_date = dateRange.to_date;
      if (selectedUser) params.user_id = selectedUser;

      const response = await axios.get(`${API_URL}/analytics/tasks`, {
        headers: { Authorization: `Bearer ${token}` }, params
      });
      setTaskAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch task analytics');
    }
  };

  const fetchTeamMemberScans = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (dateRange.from_date) params.from_date = dateRange.from_date;
      if (dateRange.to_date) params.to_date = dateRange.to_date;
      if (selectedUser) params.user_id = selectedUser;

      const response = await axios.get(`${API_URL}/daily-scans`, {
        headers: { Authorization: `Bearer ${token}` }, params
      });
      setTeamMemberScans(response.data);
    } catch (error) {
      console.error('Failed to fetch team member scans');
    }
  };

  const fetchRewardsData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, leaderboardRes, habitsRes] = await Promise.all([
        axios.get(`${API_URL}/gamification/my-stats`, { headers }),
        axios.get(`${API_URL}/gamification/leaderboard?scope=${leaderboardTab}`, { headers }),
        axios.get(`${API_URL}/gamification/habits`, { headers })
      ]);

      setUserStats(statsRes.data);
      setLeaderboard(leaderboardRes.data);
      setHabits(habitsRes.data);
    } catch (error) {
      console.error('Failed to fetch gamification data:', error);
    }
  };

  const handleApplyFilters = () => {
    setLoading(true);
    fetchAnalytics();
    fetchTaskAnalytics();
    fetchTeamMemberScans();
    setLoading(false);
  };

  const getUserName = (userId) => {
    const u = users.find(user => user.id === userId);
    return u ? u.full_name : 'Unknown User';
  };

  const handleDownload = () => {
    if (!analytics || !analytics.scans) return;
    const csvContent = [
      ['Date', 'CNESS Tasks', 'Self Care', 'R&D Learning', 'Social Media', 'Personal Time', 'Inspired Work', 'Mood'],
      ...analytics.scans.map(scan => [
        scan.date, scan.dimensions.cness_tasks.time || 0, scan.dimensions.self_care || 0,
        scan.dimensions.rnd_learning.time || 0, scan.dimensions.social_media.time || 0,
        scan.dimensions.personal_time || 0, scan.dimensions.inspired_work.time || 0, scan.mood
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics_${Date.now()}.csv`;
    a.click();
    toast.success('Report downloaded successfully');
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy size={20} style={{ color: '#F59E0B' }} />;
    if (rank === 2) return <Medal size={20} style={{ color: '#94A3B8' }} />;
    if (rank === 3) return <Medal size={20} style={{ color: '#CD7F32' }} />;
    return <span style={{ fontWeight: '700', color: 'var(--text-tertiary)' }}>#{rank}</span>;
  };

  const formatPoints = (points) => points >= 1000 ? `${(points / 1000).toFixed(1)}k` : points;

  if (loading) return <div className="analytics-page"><div className="page-header"><h2>Loading analytics...</h2></div></div>;

  const barChartData = analytics?.average_hours ? transformAverageHoursData(analytics) : [];
  const moodChartData = analytics?.mood_distribution ? transformMoodData(analytics) : [];
  const taskStatusChartData = teamMemberScans ? transformTaskStatusData(teamMemberScans, []) : [];

  return (
    <div className="analytics-page" data-testid="analytics-page">
      <div className="page-header">
        <div className="header-left">
          <h2>Analytics</h2>
          <p className="page-subtitle">Track progress, reports, and rewards</p>
        </div>
        {activeMainTab === 'analytics' && (
          <button className="gradient-btn" onClick={handleDownload} data-testid="download-report-btn">
            Download Report
          </button>
        )}
      </div>

      {/* Main Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px' }}>
        <button
          onClick={() => setActiveMainTab('analytics')}
          style={{
            padding: '10px 20px', border: 'none', borderRadius: '8px',
            background: activeMainTab === 'analytics' ? '#6366F1' : 'transparent',
            color: activeMainTab === 'analytics' ? 'white' : 'var(--text-secondary)',
            cursor: 'pointer', fontWeight: '600', fontSize: '14px',
            display: 'flex', alignItems: 'center', gap: '8px'
          }}
        >
          <BarChart3 size={16} /> Reports & Charts
        </button>
        <button
          onClick={() => setActiveMainTab('rewards')}
          style={{
            padding: '10px 20px', border: 'none', borderRadius: '8px',
            background: activeMainTab === 'rewards' ? '#6366F1' : 'transparent',
            color: activeMainTab === 'rewards' ? 'white' : 'var(--text-secondary)',
            cursor: 'pointer', fontWeight: '600', fontSize: '14px',
            display: 'flex', alignItems: 'center', gap: '8px'
          }}
        >
          <Trophy size={16} /> Activity & Rewards
        </button>
      </div>

      {activeMainTab === 'analytics' ? (
        <>
          {/* Filters */}
          <div className="filters-section card" data-testid="analytics-filters">
            <div className="filter-group">
              <label>From Date</label>
              <input type="date" value={dateRange.from_date} onChange={(e) => setDateRange({ ...dateRange, from_date: e.target.value })} />
            </div>
            <div className="filter-group">
              <label>To Date</label>
              <input type="date" value={dateRange.to_date} onChange={(e) => setDateRange({ ...dateRange, to_date: e.target.value })} />
            </div>
            {user.role !== 'team_member' && (
              <div className="filter-group">
                <label>User</label>
                <select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)}>
                  <option value="">All Users</option>
                  {users.map((u) => (<option key={u.id} value={u.id}>{u.full_name}</option>))}
                </select>
              </div>
            )}
            <button className="btn-secondary" onClick={handleApplyFilters}>Apply Filters</button>
          </div>

          {/* Team Member Submissions */}
          {teamMemberScans && teamMemberScans.length > 0 && (
            <div className="team-submissions-section" data-testid="team-submissions">
              <h3 className="section-heading">{user.role === 'team_member' ? 'My Submissions' : 'Team Member Submissions'}</h3>
              <div className="table-container card">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      {user.role !== 'team_member' && <th>Team Member</th>}
                      <th>CNESS Tasks (hrs)</th>
                      <th>Self Care (hrs)</th>
                      <th>R&D Learning (hrs)</th>
                      <th>Social Media (hrs)</th>
                      <th>Personal Time (hrs)</th>
                      <th>Inspired Work (hrs)</th>
                      <th>Mood</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamMemberScans.map((scan) => (
                      <tr key={scan.id}>
                        <td>{new Date(scan.date).toLocaleDateString()}</td>
                        {user.role !== 'team_member' && <td>{getUserName(scan.user_id)}</td>}
                        {scan.on_leave ? (
                          <>
                            <td colSpan="6" style={{ textAlign: 'center', fontWeight: 'bold', color: '#7077FE' }}>🏖️ On Leave</td>
                            <td><span className="mood-badge" style={{ background: '#f0f0ff', color: '#7077FE', border: '2px solid #7077FE' }}>On Leave</span></td>
                          </>
                        ) : (
                          <>
                            <td>{scan.dimensions?.cness_tasks?.time || 0}</td>
                            <td>{scan.dimensions?.self_care || 0}</td>
                            <td>{scan.dimensions?.rnd_learning?.time || 0}</td>
                            <td>{scan.dimensions?.social_media?.time || 0}</td>
                            <td>{scan.dimensions?.personal_time || 0}</td>
                            <td>{scan.dimensions?.inspired_work?.time || 0}</td>
                            <td><span className={`mood-badge ${scan.mood}`}>{getMoodEmoji(scan.mood)} {scan.mood}</span></td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Task Analytics */}
          {taskAnalytics && (
            <div className="task-analytics-section" data-testid="task-analytics">
              <h3 className="section-heading">Task Analytics</h3>
              <div className="summary-stats">
                <div className="stat-box card"><div className="stat-label">Total Tasks</div><div className="stat-number">{taskAnalytics.total_tasks || 0}</div></div>
                <div className="stat-box card"><div className="stat-label">Completed</div><div className="stat-number" style={{ color: '#4CAF50' }}>{taskAnalytics.completed_tasks || 0}</div></div>
                <div className="stat-box card"><div className="stat-label">In Progress</div><div className="stat-number" style={{ color: '#FF9800' }}>{taskAnalytics.in_progress_tasks || 0}</div></div>
                <div className="stat-box card"><div className="stat-label">Pending</div><div className="stat-number" style={{ color: '#F44336' }}>{taskAnalytics.pending_tasks || 0}</div></div>
                <div className="stat-box card"><div className="stat-label">Completion Rate</div><div className="stat-number">{taskAnalytics.completion_rate || 0}%</div></div>
              </div>
            </div>
          )}

          {/* Charts */}
          <div className="charts-section" data-testid="charts-section">
            <div className="chart-card card">
              <h3>Task Status Distribution</h3>
              <p className="chart-description">Task completion status over the past 7 days</p>
              <TaskStatusChart data={taskStatusChartData} loading={loading} />
            </div>
            <div className="chart-card card">
              <h3>Average Hours by Dimension</h3>
              <p className="chart-description">Average daily time allocation across dimensions</p>
              <AverageHoursChart data={barChartData} loading={loading} />
            </div>
            <div className="chart-card card">
              <h3>Mood Distribution</h3>
              <p className="chart-description">Team sentiment and wellbeing overview</p>
              <MoodDistributionChart data={moodChartData} loading={loading} />
            </div>
          </div>
        </>
      ) : (
        <>
          {/* User Stats Card */}
          <div className="card" style={{ padding: '24px', marginBottom: '24px', background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)', color: 'white' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '36px', fontWeight: '700' }}>
                  {user?.full_name?.charAt(0) || 'U'}
                </div>
                <div>
                  <h2 style={{ margin: '0 0 4px 0', fontSize: '24px', fontWeight: '700' }}>{user?.full_name || 'User'}</h2>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: 0.9 }}>
                    <Trophy size={16} />
                    <span>Rank #{userStats.rank || '-'} {leaderboardTab === 'global' ? 'globally' : 'in department'}</span>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
                <div style={{ textAlign: 'center' }}><div style={{ fontSize: '32px', fontWeight: '700' }}>{formatPoints(userStats.points)}</div><div style={{ fontSize: '13px', opacity: 0.9 }}>Total Points</div></div>
                <div style={{ textAlign: 'center' }}><div style={{ fontSize: '32px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}><Flame size={24} style={{ color: '#F59E0B' }} />{userStats.streak}</div><div style={{ fontSize: '13px', opacity: 0.9 }}>Day Streak</div></div>
                <div style={{ textAlign: 'center' }}><div style={{ fontSize: '32px', fontWeight: '700' }}>{userStats.badges?.length || 0}</div><div style={{ fontSize: '13px', opacity: 0.9 }}>Badges</div></div>
              </div>
            </div>
          </div>

          {/* Activity Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <div className="card" style={{ padding: '20px', textAlign: 'center' }}><Play size={24} style={{ color: '#6366F1', marginBottom: '8px' }} /><div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>{userStats.activity_summary?.sessions_completed || 0}</div><div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Sessions Completed</div></div>
            <div className="card" style={{ padding: '20px', textAlign: 'center' }}><Calendar size={24} style={{ color: '#10B981', marginBottom: '8px' }} /><div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>{userStats.activity_summary?.daily_scans_submitted || 0}</div><div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Daily Scans</div></div>
            <div className="card" style={{ padding: '20px', textAlign: 'center' }}><CheckCircle size={24} style={{ color: '#F59E0B', marginBottom: '8px' }} /><div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>{userStats.activity_summary?.tasks_completed || 0}</div><div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Tasks Completed</div></div>
            <div className="card" style={{ padding: '20px', textAlign: 'center' }}><Clock size={24} style={{ color: '#8B5CF6', marginBottom: '8px' }} /><div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>{(userStats.activity_summary?.total_time_hours || 0).toFixed(1)}h</div><div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Total Time</div></div>
          </div>

          {/* Leaderboard and Badges */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            <div className="card" style={{ padding: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>Leaderboard</h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => setLeaderboardTab('global')} style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: leaderboardTab === 'global' ? '#6366F1' : 'var(--bg-secondary)', color: leaderboardTab === 'global' ? 'white' : 'var(--text-secondary)', cursor: 'pointer', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}><Users size={14} />Global</button>
                  <button onClick={() => setLeaderboardTab('department')} style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: leaderboardTab === 'department' ? '#6366F1' : 'var(--bg-secondary)', color: leaderboardTab === 'department' ? 'white' : 'var(--text-secondary)', cursor: 'pointer', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}><Building2 size={14} />Department</button>
                </div>
              </div>
              {leaderboard.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-tertiary)' }}><Trophy size={40} style={{ marginBottom: '12px', opacity: 0.5 }} /><p style={{ margin: 0 }}>No leaderboard data yet</p></div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {leaderboard.slice(0, 10).map((entry, idx) => (
                    <div key={idx} style={{ padding: '12px 16px', background: entry.user_id === user?.id ? 'rgba(99, 102, 241, 0.1)' : 'var(--bg-secondary)', borderRadius: '8px', border: entry.user_id === user?.id ? '2px solid #6366F1' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '32px', textAlign: 'center' }}>{getRankIcon(idx + 1)}</div>
                        <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#6366F1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '600', fontSize: '14px' }}>{entry.name?.charAt(0) || '?'}</div>
                        <div><div style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '14px' }}>{entry.name}{entry.user_id === user?.id && <span style={{ color: '#6366F1', marginLeft: '8px' }}>(You)</span>}</div>{entry.department && <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{entry.department}</div>}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Star size={14} style={{ color: '#F59E0B' }} /><span style={{ fontWeight: '700', color: 'var(--text-primary)' }}>{formatPoints(entry.points)}</span></div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div className="card" style={{ padding: '24px' }}>
                <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>Your Badges</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                  {Object.entries(BADGES).map(([key, badge]) => {
                    const earned = userStats.badges?.includes(key);
                    return (
                      <div key={key} style={{ padding: '16px', borderRadius: '12px', background: earned ? `${badge.color}15` : 'var(--bg-secondary)', border: earned ? `2px solid ${badge.color}` : '2px dashed var(--border-color)', textAlign: 'center', minWidth: '120px', opacity: earned ? 1 : 0.5 }}>
                        <div style={{ fontSize: '32px', marginBottom: '8px' }}>{badge.icon}</div>
                        <div style={{ fontWeight: '600', fontSize: '13px', color: earned ? badge.color : 'var(--text-tertiary)' }}>{badge.name}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '4px' }}>{earned ? 'Earned!' : badge.description}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="card" style={{ padding: '24px' }}>
                <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>Weekly Activity</h3>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, idx) => {
                    const habitData = habits[idx] || { active: false };
                    return (
                      <div key={day} style={{ textAlign: 'center', flex: 1 }}>
                        <div style={{ width: '40px', height: '40px', borderRadius: '50%', margin: '0 auto 8px', background: habitData.active ? '#10B981' : 'var(--bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {habitData.active ? <CheckCircle size={20} style={{ color: 'white' }} /> : <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--border-color)' }} />}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontWeight: '500' }}>{day}</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ marginTop: '20px', padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}><Flame size={18} style={{ color: '#F59E0B' }} /><span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{userStats.streak} day streak</span></div>
                  <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '4px' }}>{userStats.streak >= 5 ? 'Amazing! Keep it up!' : `${5 - userStats.streak} more days to earn your streak badge!`}</div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Analytics;